import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {StayStatusElement} from "../../../common/uiElements/StayStatusElement";
import {InsightsCard} from "../../kit/InsightsCard";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import type {InsightsLoading} from "../../kit/insightsLoading";
import type {
    InsightRankedGuestExclusionV4,
    InsightRankedGuestV4,
} from "../../models/crm-v4";
import RankedGuestExclusionsDialog from "./RankedGuestExclusionsDialog";

/** The guest with the place it holds in this ranking, which is what the leftmost column states. */
interface RankedRow {
    key: string;
    rank: number;
    guest: InsightRankedGuestV4;
}

export interface RankedGuestTableProps {
    title: string;
    hint: string;
    valueLabel: string;
    rows: InsightRankedGuestV4[];
    comparisonRows?: InsightRankedGuestV4[];
    loading?: InsightsLoading;
    formatValue: (value: number) => string;
    exclusions: InsightRankedGuestExclusionV4[];
    exclusionsLoading: boolean;
    exclusionsError: boolean;
    actionLoading: boolean;
    onExclude: (guest: InsightRankedGuestV4) => void;
    onRestore: (exclusion: InsightRankedGuestExclusionV4) => void;
}

/**
 * The two top-guest rankings, read as a table rather than a bespoke list: same columns, alignment and
 * row rhythm as every other insights table, so a leaderboard and a distribution are the same object
 * with different rows. StayStatusElement keeps chip semantics app-wide.
 */
export function RankedGuestTable({
    title,
    hint,
    valueLabel,
    rows,
    comparisonRows,
    loading,
    formatValue,
    exclusions,
    exclusionsLoading,
    exclusionsError,
    actionLoading,
    onExclude,
    onRestore,
}: RankedGuestTableProps) {
    const {t} = useTranslation();
    const [rowMenu, setRowMenu] = useState<{anchorEl: HTMLElement; guest: InsightRankedGuestV4} | null>(null);
    const [listMenuAnchor, setListMenuAnchor] = useState<HTMLElement | null>(null);
    const [exclusionsOpen, setExclusionsOpen] = useState(false);
    const excludedProfileIds = new Set(exclusions.map(exclusion => exclusion.profileId));
    const hasComparison = comparisonRows !== undefined;

    const columns: InsightsColumn<RankedRow>[] = [
        {
            key: "rank",
            label: "#",
            kind: "text",
            width: 32,
            cell: row => (
                <Typography variant={"body1"} color={"text.disabled"} sx={{fontVariantNumeric: "tabular-nums"}}>
                    {row.rank}
                </Typography>
            ),
        },
        {
            key: "guest",
            label: t("Guest"),
            kind: "label",
            cell: row => (
                <Stack direction={"row"} alignItems={"center"} flexWrap={"wrap"} gap={0.75} sx={{minWidth: 0}}>
                    <Typography variant={"body1"} fontWeight={600} noWrap sx={{minWidth: 0}}>
                        {row.guest.name}
                    </Typography>

                    {row.guest.stay && (
                        <StayStatusElement
                            stayStatus={row.guest.stay.status}
                            startDate={row.guest.stay.from}
                            endDate={row.guest.stay.to}
                        />
                    )}
                </Stack>
            ),
        },
        {
            key: "value",
            label: valueLabel,
            kind: "value",
            cell: row => formatValue(row.guest.value),
        },
        {
            key: "actions",
            label: "",
            kind: "text",
            width: 44,
            cell: row => (
                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                    <IconButton
                        size={"small"}
                        aria-label={`${t("More actions")}: ${row.guest.name}`}
                        disabled={actionLoading}
                        onClick={event => setRowMenu({anchorEl: event.currentTarget, guest: row.guest})}
                        sx={{color: "text.disabled", "&:hover": {color: "text.primary"}}}
                    >
                        <MoreHorizIcon fontSize={"small"}/>
                    </IconButton>
                </Box>
            ),
        },
    ];

    const ranking = (allRows: InsightRankedGuestV4[], periodLabel?: string) => {
        const visible = allRows
            .filter(row => !excludedProfileIds.has(row.profileId))
            .map((guest, index) => ({key: guest.profileId, rank: index + 1, guest}));

        return (
            <Box sx={{minWidth: 0}}>
                {periodLabel && (
                    <Typography
                        component={"div"}
                        variant={"caption"}
                        color={"text.secondary"}
                        fontWeight={600}
                        sx={{pb: 0.75}}
                    >
                        {periodLabel}
                    </Typography>
                )}

                {visible.length === 0 ? (
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"} sx={{py: 3}}>
                        {t(periodLabel ? "No data in this period" : "No data in the selected period")}
                    </Typography>
                ) : (
                    <InsightsTable
                        label={periodLabel ? `${title} — ${periodLabel}` : title}
                        columns={columns}
                        rows={visible}
                        rowKey={row => row.key}
                    />
                )}
            </Box>
        );
    };

    return (
        <>
            <InsightsCard
                title={title}
                hint={hint}
                loading={loading}
                actions={(
                    <IconButton
                        size={"small"}
                        aria-label={t("List options")}
                        onClick={event => setListMenuAnchor(event.currentTarget)}
                    >
                        <MoreHorizIcon/>
                    </IconButton>
                )}
            >
                <Box
                    sx={{
                        display: "grid",
                        // Two rankings side by side: the comparison period ranks different guests, so it is
                        // its own list rather than a column added to this one.
                        gridTemplateColumns: hasComparison ? {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))"} : "1fr",
                        gap: hasComparison ? 2.5 : 0,
                        minWidth: 0,
                    }}
                >
                    {ranking(rows, hasComparison ? t("Selected period") : undefined)}
                    {comparisonRows !== undefined && ranking(comparisonRows, t("Comparison period"))}
                </Box>
            </InsightsCard>

            <Menu
                anchorEl={rowMenu?.anchorEl}
                open={Boolean(rowMenu)}
                onClose={() => setRowMenu(null)}
            >
                <MenuItem
                    disabled={actionLoading}
                    onClick={() => {
                        if (rowMenu) onExclude(rowMenu.guest);
                        setRowMenu(null);
                    }}
                >
                    <ListItemIcon><PersonOffOutlinedIcon fontSize="small"/></ListItemIcon>
                    {t("Exclude from this list")}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={listMenuAnchor}
                open={Boolean(listMenuAnchor)}
                onClose={() => setListMenuAnchor(null)}
            >
                <MenuItem
                    disabled={exclusionsLoading}
                    onClick={() => {
                        setListMenuAnchor(null);
                        setExclusionsOpen(true);
                    }}
                >
                    <ListItemIcon><PersonOffOutlinedIcon fontSize="small"/></ListItemIcon>
                    {t("Manage excluded profiles")}
                </MenuItem>
            </Menu>

            <RankedGuestExclusionsDialog
                open={exclusionsOpen}
                listTitle={title}
                exclusions={exclusions}
                loading={exclusionsLoading}
                updating={actionLoading}
                error={exclusionsError}
                onRestore={onRestore}
                onClose={() => setExclusionsOpen(false)}
            />
        </>
    );
}

export default RankedGuestTable;
